import React from 'react'
import styled, { keyframes } from 'styled-components'

// ============================================
// Animated Heading Components
// ============================================

// Animation keyframes
const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`

const gradientShift = keyframes`
    0% {
        background-position: 0% 50%;
    }
    50% {
        background-position: 100% 50%;
    }
    100% {
        background-position: 0% 50%;
    }
`

const underlineExpand = keyframes`
    from {
        width: 0;
    }
    to {
        width: 100%;
    }
`

// ============================================
// Main Section Heading (H1/H2) - For major sections
// ============================================
export const SectionHeading = styled.h2`
    font-size: 2.8rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: ${props => props.noMargin ? '0' : '20px'};
    line-height: 1.2;
    position: relative;
    
    /* Gradient text effect */
    background: linear-gradient(135deg, rgb(43, 121, 160) 0%, #58bcee 50%, rgb(43, 121, 160) 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    
    /* Animated gradient */
    animation: ${gradientShift} 3s ease infinite;

    /* Fade in animation */
    opacity: 0;
    animation: ${fadeInUp} 0.8s ease forwards, ${gradientShift} 3s ease infinite;

    @media (max-width: 768px) {
        font-size: 2rem;
    }

    @media (max-width: 480px) {
        font-size: 1.8rem;
    }
`

// ============================================
// Section Heading with Underline
// ============================================
export const SectionHeadingUnderline = styled.h2`
    font-size: 2.8rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: ${props => props.noMargin ? '0' : '30px'};
    line-height: 1.2;
    position: relative;
    display: inline-block;
    
    /* Fade in animation */
    opacity: 0;
    animation: ${fadeInUp} 0.8s ease forwards;

    /* Animated underline */
    &::after {
        content: '';
        position: absolute;
        bottom: -10px;
        left: 0;
        height: 4px;
        width: 0;
        background: linear-gradient(90deg, rgb(43, 121, 160) 0%, #58bcee 100%);
        border-radius: 2px;
        animation: ${underlineExpand} 0.8s ease 0.3s forwards;
    }

    @media (max-width: 768px) {
        font-size: 2rem;
    }

    @media (max-width: 480px) {
        font-size: 1.8rem;
    }
`

// ============================================
// Sub Section Heading (H3) - For subsections
// ============================================
export const SubHeading = styled.h3`
    font-size: 1.8rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: ${props => props.noMargin ? '0' : '15px'};
    line-height: 1.3;
    position: relative;
    padding-left: ${props => props.withAccent ? '20px' : '0'};
    
    /* Fade in animation */
    opacity: 0;
    animation: ${fadeInUp} 0.8s ease forwards;

    /* Optional accent line */
    ${props => props.withAccent && `
        &::before {
            content: '';
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            width: 5px;
            height: 70%;
            background: linear-gradient(180deg, rgb(43, 121, 160) 0%, #58bcee 100%);
            border-radius: 3px;
        }
    `}

    @media (max-width: 768px) {
        font-size: 1.5rem;
    }

    @media (max-width: 480px) {
        font-size: 1.3rem;
    }
`

// ============================================
// Card Heading (H4) - For cards and smaller sections
// ============================================
export const CardHeading = styled.h4`
    font-size: 1.4rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: ${props => props.noMargin ? '0' : '10px'};
    line-height: 1.3;
    
    /* Fade in animation */
    opacity: 0;
    animation: ${fadeInUp} 0.6s ease forwards;

    /* Hover effect */
    transition: color 0.3s ease;
    cursor: ${props => props.clickable ? 'pointer' : 'default'};

    ${props => props.clickable && `
        &:hover {
            color: rgb(43, 121, 160);
        }
    `}

    @media (max-width: 768px) {
        font-size: 1.2rem;
    }

    @media (max-width: 480px) {
        font-size: 1.1rem;
    }
`

// ============================================
// Badge/Label Heading - Small uppercase heading
// ============================================
export const BadgeHeading = styled.span`
    display: inline-block;
    background: linear-gradient(135deg, rgb(43, 121, 160) 0%, #58bcee 100%);
    color: white;
    padding: 8px 20px;
    border-radius: 50px;
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: ${props => props.noMargin ? '0' : '20px'};
    
    /* Fade in animation */
    opacity: 0;
    animation: ${fadeInUp} 0.6s ease forwards;

    /* Subtle pulse effect */
    &:hover {
        transform: scale(1.05);
        box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
    }

    transition: all 0.3s ease;

    @media (max-width: 768px) {
        font-size: 0.8rem;
        padding: 6px 16px;
    }
`

// ============================================
// Heading Container - Wraps heading with description
// ============================================
export const HeadingContainer = styled.div`
    text-align: ${props => props.align || 'center'};
    margin-bottom: ${props => props.spacing || '60px'};
    max-width: ${props => props.maxWidth || '800px'};
    margin-left: auto;
    margin-right: auto;

    @media (max-width: 768px) {
        margin-bottom: ${props => props.spacing ? '40px' : '40px'};
        text-align: ${props => props.align === 'left' ? 'left' : 'center'};
    }
`

// ============================================
// Description Text - Pairs with headings
// ============================================
export const HeadingDescription = styled.p`
    font-size: 1.1rem;
    color: #000;
    line-height: 1.8;
    margin-top: ${props => props.noMargin ? '0' : '15px'};
    max-width: ${props => props.maxWidth || '700px'};
    margin-left: auto;
    margin-right: auto;
    
    /* Fade in animation */
    opacity: 0;
    animation: ${fadeInUp} 0.8s ease 0.2s forwards;

    @media (max-width: 768px) {
        font-size: 1rem;
    }

    @media (max-width: 480px) {
        font-size: 0.95rem;
    }
`

// ============================================
// Gradient Text - For emphasis within text
// ============================================
export const GradientText = styled.span`
    background: linear-gradient(135deg, rgb(43, 121, 160) 0%, #58bcee 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 700;
`

// ============================================
// Animated Heading with Icon
// ============================================
export const IconHeading = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    margin-bottom: ${props => props.noMargin ? '0' : '20px'};
    
    /* Fade in animation */
    opacity: 0;
    animation: ${fadeInUp} 0.8s ease forwards;

    .icon-wrapper {
        width: 50px;
        height: 50px;
        min-width: 50px;
        border-radius: 50%;
        background: linear-gradient(135deg, rgb(43, 121, 160) 0%,#58bcee 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
        
        svg {
            color: white;
            font-size: 1.5rem;
        }

        /* Pulse animation on hover */
        transition: transform 0.3s ease;
        &:hover {
            transform: scale(1.1);
        }
    }

    h3 {
        font-size: 1.8rem;
        font-weight: 700;
        color: #2c3e50;
        margin: 0;
    }

    @media (max-width: 768px) {
        .icon-wrapper {
            width: 40px;
            height: 40px;
            min-width: 40px;

            svg {
                font-size: 1.2rem;
            }
        }

        h3 {
            font-size: 1.5rem;
        }
    }
`

// ============================================
// Export all as default object
// ============================================
export default {
    SectionHeading,
    SectionHeadingUnderline,
    SubHeading,
    CardHeading,
    BadgeHeading,
    HeadingContainer,
    HeadingDescription,
    GradientText,
    IconHeading
}